<script>
import TabBar from './tab-bar'
import sIcon from '../icon'						   
import { addResizeListener, removeResizeListener } from '../../utils/resize-event'

function noop() {}

export default {
  name: 'sTabNav',
  data() {
    return {
      scrollable: false,
      navOffset: 0,
      isFocus: false,
      focusable: true
    }
  },
  components: {
    TabBar
  },
  inject: ['rootTabs'],
  props: {
    panes: Array,
    currentName: [String, Number],
    editable: Boolean,
    onTabClick: {
      type: Function,
      default: noop
    },
    onTabRemove: {
      type: Function,
      default: noop
    },
    type: String,
    stretch: Boolean
  },
  computed: {
    navStyle() {
      return {
        transform: `translateX(-${this.navOffset}px)`
      }
    },
  },
  methods: {
    scrollPrev() {
      const containerSize = this.$refs.navScroll.offsetWidth
      const currentOffset = this.navOffset

      if (!currentOffset) { return }

      let newOffset = currentOffset > containerSize
        ? currentOffset - containerSize
        : 0

      this.navOffset = newOffset
    },
    scrollNext() {
      const navSize = this.$refs.nav.offsetWidth
      const containerSize = this.$refs.navScroll.offsetWidth
      const currentOffset = this.navOffset

      if (navSize - currentOffset <= containerSize) { return }

      let newOffset = navSize - currentOffset > containerSize * 2
        ? currentOffset + containerSize
        : (navSize - containerSize)

      this.navOffset = newOffset
    },
    scrollToActiveTab() {
      if (!this.scrollable) { return }
      const nav = this.$refs.nav
      const activeTab = this.$el.querySelector('.is-active')
      if (!activeTab) { return }
      const navScroll = this.$refs.navScroll
      const activeTabBounding = activeTab.getBoundingClientRect()
      const navScrollBounding = navScroll.getBoundingClientRect()
      const maxOffset = nav.offsetWidth - navScrollBounding.width
      const currentOffset = this.navOffset
      let newOffset = currentOffset

      if (activeTabBounding.left < navScrollBounding.left) {
        newOffset = currentOffset - (navScrollBounding.left - activeTabBounding.left)
      }
      if (activeTabBounding.right > navScrollBounding.right) {
        newOffset = currentOffset + activeTabBounding.right - navScrollBounding.right
      }
      newOffset = Math.max(newOffset, 0)
      this.navOffset = Math.min(newOffset, maxOffset)
    },
    update() {
      if (!this.$refs.nav) { return }
      const navSize = this.$refs.nav.offsetWidth
      const containerSize = this.$refs.navScroll.offsetWidth
      const currentOffset = this.navOffset

      if (containerSize < navSize) {
        const prevValue = currentOffset
        const nextValue = currentOffset + containerSize < navSize
        const scrollable = this.scrollable
        if (!scrollable || scrollable.prev !== prevValue || scrollable.next !== nextValue) {
          this.scrollable = {
            prev: prevValue,
            next: nextValue
          }
        }
        if (navSize - currentOffset < containerSize) {
          this.navOffset = navSize - containerSize
        }
      } else {
        if (this.scrollable !== false) {
          this.scrollable = false
        }
        if (currentOffset > 0) {
          this.navOffset = 0
        }
      }
    },
    changeTab(e) {
      const keyCode = e.keyCode
      let nextIndex
      let currentIndex, tabList
      if ([37, 38, 39, 40].indexOf(keyCode) !== -1) { // 左右上下键更换tab
        tabList = e.currentTarget.querySelectorAll('[role=tab]')
        currentIndex = Array.prototype.indexOf.call(tabList, e.target)
      } else {
        return
      }
      if (keyCode === 37 || keyCode === 38) { // left
        if (currentIndex === 0) { // first
          nextIndex = tabList.length - 1
        } else {
          nextIndex = currentIndex - 1
        }
      } else { // right
        if (currentIndex < tabList.length - 1) { // not last
          nextIndex = currentIndex + 1
        } else {
          nextIndex = 0
        }
      }
      tabList[nextIndex].focus() // 改变焦点元素
      tabList[nextIndex].click() // 选中下一个tab
      this.setFocus()
    },
    setFocus() {
      if (this.focusable) {
        this.isFocus = true
      }
    },
    removeFocus() {
      this.isFocus = false
    },
    visibilityChangeHandler() {
      const visibility = document.visibilityState
      if (visibility === 'hidden') {
        this.focusable = false
      } else if (visibility === 'visible') {
        setTimeout(() => {
          this.focusable = true
        }, 50)
      }
    },
    windowBlurHandler() {
      this.focusable = false
    },
    windowFocusHandler() {
      setTimeout(() => {
        this.focusable = true
      }, 50)
    }
  },

  updated() {
    this.update()
  },

  render() {
    const {
      panes,
      editable,
      stretch,
      onTabClick,
      onTabRemove,
      navStyle,
      scrollable,
      scrollNext,
      scrollPrev,
      changeTab,
      setFocus,
      removeFocus
    } = this
    const scrollBtn = scrollable
      ? [
        <span class={['s-tabs__nav-prev', scrollable.prev ? '' : 'is-disabled']} on-click={scrollPrev}><sIcon type="arrow" rotate="90"/></span>,
        <span class={['s-tabs__nav-next', scrollable.next ? '' : 'is-disabled']} on-click={scrollNext}><sIcon type="arrow" rotate="-90"/></span>
      ] : null

    const tabs = this._l(panes, (pane, index) => {
      let tabName = pane.name || pane.index || index
      const closable = pane.isClosable || editable

      pane.index = `${index}`

      const btnClose = closable
        ? <span class="s-icon-close" on-click={(ev) => { onTabRemove(pane, ev) }}></span>
        : null

      const tabLabelContent = (pane.$scopedSlots && pane.$scopedSlots.label ? pane.$scopedSlots.label() : null) || pane.$slots.label || pane.label
      const tabindex = pane.active ? 0 : -1
      return (
        <div
          class={{
            's-tabs__item': true,
            'is-top': true,
            'is-active': pane.active,
            'is-disabled': pane.disabled,
            'is-closable': closable,
            'is-focus': this.isFocus
          }}
          id={`tab-${tabName}`}
          key={`tab-${tabName}`}
          aria-controls={`pane-${tabName}`}
          role="tab"
          aria-selected={ pane.active }
          ref="tabs"
          tabindex={tabindex}
          refInFor
          on-focus={ () => { setFocus() }}
          on-blur ={ () => { removeFocus() }}
          on-click={(ev) => { removeFocus(); onTabClick(pane, tabName, ev) }}
          on-keydown={(ev) => { if (closable && (ev.keyCode === 46 || ev.keyCode === 8)) { onTabRemove(pane, ev) } }}
        >
          {tabLabelContent}
          {btnClose}
        </div>
      )
    })
    return (
      <div class={['s-tabs__nav-wrap', scrollable ? 'is-scrollable' : '', 'is-top']}>
        {scrollBtn}
        <div class={['s-tabs__nav-scroll']} ref="navScroll">
          <div
            class={['s-tabs__nav', 'is-top', stretch && 'is-stretch']}
            ref="nav"
            style={navStyle}
            role="tablist"
            on-keydown={ changeTab }
          >
            <tab-bar tabs={panes}></tab-bar>
            {tabs}
          </div>
        </div>
      </div>
    )
  },

  mounted() {
    addResizeListener(this.$el, this.update)
    document.addEventListener('visibilitychange', this.visibilityChangeHandler)
    window.addEventListener('blur', this.windowBlurHandler)
    window.addEventListener('focus', this.windowFocusHandler)
    setTimeout(() => {
      this.scrollToActiveTab()
    }, 0)
  },

  beforeDestroy() {
    if (this.$el && this.update) { removeResizeListener(this.$el, this.update) }
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler)
    window.removeEventListener('blur', this.windowBlurHandler)
    window.removeEventListener('focus', this.windowFocusHandler)
  }
}
</script>